import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import {
  Assessment,
  Warning,
  CheckCircle,
} from '@mui/icons-material';

interface UsageStatsProps {
  usageStats?: {
    report_count: number;
    limit: number;
    limit_reached: boolean;
    remaining: number;
  };
  isLoading?: boolean;
  error?: any;
}

const UsageStats: React.FC<UsageStatsProps> = ({ usageStats, isLoading, error }) => {
  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            📊 Usage Statistics
          </Typography>
          <Alert severity="error">
            <Typography variant="body2">
              Failed to load usage statistics. Please refresh the page or contact support.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isLoading || !usageStats) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            📊 Usage Statistics
          </Typography>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="textSecondary">
              Loading usage statistics...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const progressPercentage = (usageStats.report_count / usageStats.limit) * 100;
  const isNearLimit = progressPercentage >= 80;
  const isAtLimit = usageStats.limit_reached;

  return (
    <Card 
      sx={{ 
        background: isAtLimit ? 
          'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)' : 
          isNearLimit ? 
            'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' :
            'linear-gradient(135deg, #FF9933 0%, #138808 100%)',
        color: 'white',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {isAtLimit ? (
            <Warning sx={{ mr: 1, fontSize: 28 }} />
          ) : (
            <Assessment sx={{ mr: 1, fontSize: 28 }} />
          )}
          <Typography variant="h6" component="h2">
            Usage Statistics
          </Typography>
        </Box>

        {/* Usage Counter */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h2" component="div" sx={{ fontWeight: 700, mb: 1 }}>
            {isAtLimit ? '⚠️' : '📊'} {usageStats.report_count}/{usageStats.limit}
          </Typography>
          <Typography variant="body1">
            {isAtLimit ? 'LIMIT REACHED' : 'Reports Analyzed'}
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: isAtLimit ? '#ffcdd2' : isNearLimit ? '#fff3e0' : '#ffffff',
              },
            }}
          />
        </Box>

        {/* Status Message */}
        {isAtLimit ? (
          <Alert 
            severity="error" 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: '#d32f2f',
              '& .MuiAlert-icon': { color: '#d32f2f' }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Free limit reached! Contact info@2ndopinionai.org for upgrade options.
            </Typography>
          </Alert>
        ) : isNearLimit ? (
          <Alert 
            severity="warning"
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: '#f57c00',
              '& .MuiAlert-icon': { color: '#f57c00' }
            }}
          >
            <Typography variant="body2">
              <strong>{usageStats.remaining} analyses remaining</strong> before reaching the free limit.
            </Typography>
          </Alert>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Chip
              icon={<CheckCircle />}
              label={`${usageStats.remaining} analyses remaining`}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          </Box>
        )}

        {/* Additional Information */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Free tier includes {usageStats.limit} medical report analyses
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UsageStats;




